import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { touch } from 'redux-form';
import omit from 'lodash/omit';
import {
  getQuickCheckoutAgreementPolicy,
  areQuickCheckoutAgreementsDirty,
  selectQuickCheckoutAgreementAccount,
} from 'utils/purchase/quickCheckoutAgreements';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import {
  quickPurchaseMainButtonTracker,
  quickPurchasePaymentAttemptTracker,
} from 'metrics/user-analytics/purchase';
import { isAddiBlockedByDocument } from 'utils/purchase/addiDocumentValidation';
import buildPaymentAttemptDataAudit from 'utils/purchase/paymentAttemptDataAudit';
import selectIsPurchaserFormValid from 'utils/purchase/selectIsPurchaserFormValid';
import {
  expectedPaymentCancellation,
  isExpectedPaymentCancellation,
} from 'utils/purchase/expectedPaymentCancellation';
import { selectGontijoPaymentBlocked } from 'selectors/gontijoSessionSelectors';
import basisTheoryCheckoutEngine from 'payments/core/engines/basisTheory';
import { isGontijoPaymentBlockedNow } from '@/services/gontijo-session/authorizedPurchaseApi';
import {
  selectQuickCheckoutPurchaserSaveValues,
  selectQuickCheckoutPurchaserFormValues,
} from './useQuickCheckoutState';
import { purchaserValuesMatch } from './usePurchaserSaveState';
import usePaymentEngines from './usePaymentEngines';
import usePaymentHandler from './usePaymentHandler';

/**
 * PaymentOptions passes payment params; PaymentButton passes the click event.
 * @param {object|Event} paymentParams - Payment params or DOM click event.
 * @returns {object} Normalized payment params.
 */
const normalizePaymentClickParams = (paymentParams) => {
  if (paymentParams && typeof paymentParams === 'object' && 'nativeEvent' in paymentParams) {
    return {};
  }

  return paymentParams || {};
};

/**
 * Indicates whether the payment option uses a native wallet sheet (Apple Pay / Google Pay).
 * @param {string} paymentOption - Selected payment option.
 * @returns {boolean} Whether the option is a wallet payment.
 */
const isWalletPaymentOption = (paymentOption) =>
  paymentOption === 'apple_pay' || paymentOption === 'google_pay';

/**
 * usePaymentSection hook.
 * Consolidate all payment-related state and handlers for the payment section UI.
 * Internally initializes payment engine hooks and the payment handler, exposing
 * only the callbacks and flags needed for rendering PaymentPanel.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object (includes token, total, selectedPaymentOption, etc.).
 * @param {object} params.env - Environment configuration.
 * @param {object} params.features - Whitelabel feature flags.
 * @param {object} params.purchaser - Purchaser data derived from form/store.
 * @param {object} params.purchaserFormValues - Raw redux-form values for the purchaser form.
 * @param {object} params.actions - Action callbacks from useQuickCheckoutActions.
 * @param {object} params.cardValues - Card form values.
 * @param {boolean} params.fetchingPayment - Whether a payment confirmation is in flight.
 * @param {boolean} params.isUpdating - Whether the purchase is being updated.
 * @param {object} params.selectedPaymentMethod - Currently selected payment method.
 * @param {'basis_theory'|'legacy'} params.cardCheckoutMode - Owner of card capture and submission.
 * @param {string} params.paymentEngine - Payment engine identifier.
 * @param {boolean} params.isOrchestrating - Whether quick checkout setup is in progress.
 * @param {object} params.orchestrationError - Quick checkout setup error, if any.
 * @param {boolean} params.purchaserSubmitted - Whether the purchaser form still equals what the last successful save sent (owned by parent). False again as soon as the buyer edits a saved field.
 * @param {Function} params.onPurchaserSubmitted - Callback to notify the parent that the purchaser was saved, called with the values that save sent.
 * @param {Function} [params.onBeforePaymentAsync] - Optional async callback that must resolve before payment proceeds.
 * @param {object} [params.quickCheckoutAgreementPolicy] - Enables current Quick Purchase agreement checks.
 * @param {boolean} [params.legalTerms] - Current rendered legal acceptance for the wallet overlay.
 * @returns {object} Payment section handlers, loading flags, and the Yape form props.
 */
function usePaymentSection({
  purchase,
  env,
  features,
  purchaser,
  purchaserFormValues,
  actions,
  cardValues,
  fetchingPayment,
  isUpdating,
  selectedPaymentMethod,
  cardCheckoutMode,
  paymentEngine,
  isOrchestrating,
  orchestrationError,
  purchaserSubmitted,
  onPurchaserSubmitted,
  onBeforePaymentAsync,
  quickCheckoutAgreementPolicy,
  legalTerms,
}) {
  const { token, total, updatingInsurance, selectedPaymentOption, fetchingTaxpayerId } = purchase;
  const store = useStore();
  const currentRef = useRef(null);
  currentRef.current = {
    purchase,
    purchaserFormValues,
    quickCheckoutAgreementPolicy,
    legalTerms,
    actions,
    features,
    env,
    onPurchaserSubmitted,
    onBeforePaymentAsync,
    purchaserSubmitted,
  };
  const purchaserSavePromiseRef = useRef(null);
  const walletNeedsPassengerSaveRef = useRef(false);
  const [pendingAgreements, setPendingAgreements] = useState(false);
  const pendingAgreementParamsRef = useRef(null);
  const pendingAgreementOptionRef = useRef(null);

  // Read the store after every await: a checkbox or passenger mirror may change without
  // a payment callback being replaced, including while the wallet SDK owns the gesture.
  const readCurrentAgreements = useCallback(() => {
    const { current } = currentRef;
    const state = store.getState();
    const config = state.whitelabelConfig || {};
    const currentFeatures = config.features || current.features;
    const storedMeta = state.purchase?.get?.('meta');
    const currentPurchase = state.purchase?.get
      ? {
          token: state.purchase.get('token'),
          meta: storedMeta?.toJS ? storedMeta.toJS() : storedMeta,
        }
      : state.purchase || current.purchase;
    const values = state.form
      ? selectQuickCheckoutPurchaserSaveValues(
          state,
          Boolean(current.quickCheckoutAgreementPolicy) &&
            currentFeatures.PURCHASER_FORM_ON !== 'CHECKOUT',
        )
      : current.purchaserFormValues;
    const policy = current.quickCheckoutAgreementPolicy
      ? getQuickCheckoutAgreementPolicy({
          ...selectQuickCheckoutAgreementAccount(state),
          features: currentFeatures,
          brand: config.env?.brand || current.env.brand,
          cdsSession: state.cdsFrequentPassenger?.toJS
            ? state.cdsFrequentPassenger.toJS()
            : state.cdsFrequentPassenger || {},
        })
      : {};
    return {
      purchase: currentPurchase,
      values: values || {},
      formValues: state.form
        ? selectQuickCheckoutPurchaserFormValues(state) || {}
        : current.purchaserFormValues,
      policy,
      legal: state.form ? state.form.purchaser?.values?.legalTerms : current.legalTerms,
      dirty: areQuickCheckoutAgreementsDirty({
        policy,
        purchaserFormValues: values || {},
        meta: currentPurchase.meta,
      }),
    };
  }, [store]);

  const requireCurrentLegal = useCallback(() => {
    const current = readCurrentAgreements();
    if (current.policy.legalRequired && !current.legal) {
      store.dispatch(touch('purchaser', 'legalTerms'));
      currentRef.current.actions.onFormFail();
      throw expectedPaymentCancellation('legal_terms_revoked');
    }
    return current;
  }, [readCurrentAgreements, store]);

  // One canonical PUT at a time; a choice changed in flight is saved on the next pass.
  // Wallet confirmations await this promise; redux-form keeps its fire-and-forget callback.
  const synchronizePurchaser = useCallback(
    (forceSave = false) => {
      if (purchaserSavePromiseRef.current) return purchaserSavePromiseRef.current;
      /**
       * Persist consecutive snapshots until the current optional choices match canonical meta.
       * @returns {Promise<object>} Current values after every required purchase PUT.
       */
      const synchronize = async () => {
        let current = requireCurrentLegal();
        let shouldSave = forceSave || current.dirty;
        while (shouldSave) {
          const sentFormValues = current.formValues;
          // Later writes depend on meta returned by the previous canonical PUT.
          // eslint-disable-next-line no-await-in-loop
          await currentRef.current.actions.onSetPurchaser(current.purchase.token, current.values);
          currentRef.current.onPurchaserSubmitted?.(current.values);
          current = requireCurrentLegal();
          // Buyer edits cancel this attempt, while optional choices still synchronize before paying.
          const choiceFields = ['promotionalTerms', 'cdsFrequentPassengerOptIn'];
          if (
            !purchaserValuesMatch(
              omit(sentFormValues, choiceFields),
              omit(current.formValues, choiceFields),
            )
          ) {
            throw expectedPaymentCancellation('purchaser_edited_during_save');
          }
          shouldSave = current.dirty;
        }
        return current.values;
      };
      const promise = synchronize();
      purchaserSavePromiseRef.current = promise;
      promise.then(
        () => {
          purchaserSavePromiseRef.current = null;
        },
        () => {
          purchaserSavePromiseRef.current = null;
        },
      );
      return promise;
    },
    [requireCurrentLegal],
  );

  const beforeWalletPayment = useCallback(async () => {
    requireCurrentLegal();
    if (isGontijoPaymentBlockedNow()) throw expectedPaymentCancellation('session_expired');
    if (walletNeedsPassengerSaveRef.current) {
      walletNeedsPassengerSaveRef.current = false;
      try {
        await currentRef.current.onBeforePaymentAsync?.();
      } catch (error) {
        if (!isExpectedPaymentCancellation(error)) {
          errorMonitoring.notify({
            error,
            info: { from: 'usePaymentSection:onBeforePaymentAsync' },
          });
        }
        throw error;
      }
    }
    if (isGontijoPaymentBlockedNow()) throw expectedPaymentCancellation('session_expired');
    await synchronizePurchaser(
      currentRef.current.features.PURCHASER_FORM_ON === 'CHECKOUT' &&
        !currentRef.current.purchaserSubmitted,
    );
    // Recheck after sharing a pending PUT as well as after our own save.
    while (requireCurrentLegal().dirty) {
      // A shared PUT may have settled before this confirmation resumed.
      // eslint-disable-next-line no-await-in-loop
      await synchronizePurchaser();
    }
    if (isGontijoPaymentBlockedNow()) throw expectedPaymentCancellation('session_expired');
  }, [requireCurrentLegal, synchronizePurchaser]);

  const synchronizePurchaserAndResume = useCallback(
    (params) => {
      const paymentOption = currentRef.current.purchase.selectedPaymentOption;
      synchronizePurchaser()
        .then(() => {
          if (paymentOption !== currentRef.current.purchase.selectedPaymentOption) {
            params.reject?.();
            return;
          }
          pendingAgreementParamsRef.current = params;
          pendingAgreementOptionRef.current = paymentOption;
          setPendingAgreements(true);
        })
        .catch((error) => {
          params.reject?.(error);
        });
    },
    [synchronizePurchaser],
  );

  // Registration-independent buyer validity, same selector the regular Checkout and the wallet
  // buttons gate on (CheckoutContainer, WalletButtonContainer). `purchaserSubmitted` cannot stand in
  // for it: it only means "saved and unchanged since", never "valid".
  const isPurchaserFormValid = useSelector(selectIsPurchaserFormValid);
  const [isSyncingPurchaserForWallet, setIsSyncingPurchaserForWallet] = useState(false);
  const [basisTheoryStatus, setBasisTheoryStatus] = useState(basisTheoryCheckoutEngine.getStatus());
  const pendingPaymentParamsRef = useRef(null);
  const pendingWalletPaymentParamsRef = useRef(null);
  // The payment option the parked payment was clicked under (PRO2-146, design D3). Resuming reads
  // the option selected at that moment, so a payment whose option changed while its save was in
  // flight is dropped instead of started under a method the buyer never clicked.
  const pendingPaymentOptionRef = useRef(null);
  const autoSubmittedPurchaserRef = useRef(false);
  const failedAutoSubmissionRef = useRef(null);
  // The buyer form as of the latest render, read when a purchaser save settles to tell whether the
  // buyer edited it while the request was in flight (PRO2-146). Assigned during render rather than
  // in an effect so a save settling before passive effects flush still sees the edit.
  const latestPurchaserFormValuesRef = useRef(purchaserFormValues);
  latestPurchaserFormValuesRef.current = purchaserFormValues;
  // Purchaser saves started and not yet settled. The resume effect waits for zero, so a parked
  // payment resumes only after its save has settled and been checked against the form, never on a
  // render that happens to land while it is in flight (PRO2-146). A count, not a flag: the PayPal
  // save on selection and a pay-click save can overlap.
  const purchaserSavesInFlightRef = useRef(0);
  // Bumped when the last in-flight save settles with a payment parked, so the resume effect re-runs
  // then. Nothing else it depends on has to change at that point: with two overlapping saves of the
  // same values, the first already flipped `purchaserSubmitted` and the second records an identical
  // snapshot, which re-renders nothing.
  const [lastPurchaserSaveSettled, setLastPurchaserSaveSettled] = useState(0);
  // Pending payment params for the passenger-edit path. Unlike the synchronous
  // purchaser path, payment here must resume AFTER the passenger save mutates the
  // store and re-renders QuickCheckout. Resuming inside the onBeforePaymentAsync
  // .then() would run continuePayment/handlePayment with stale click-time closures
  // (old purchase, engineHooks, isUpdating), so the payment silently no-ops. We defer
  // to an effect that fires from a fresh render once payment is unblocked.
  const pendingAfterPassengersParamsRef = useRef(null);
  const [pendingAfterPassengers, setPendingAfterPassengers] = useState(false);
  // What YapeForm reports, read by the Yape payment handler at click time (PRO2-147). A ref rather
  // than state: nothing renders from it, and state would re-render the whole payment panel on every
  // keystroke in the Yape inputs.
  const yapeFormRef = useRef({ formData: null, isValid: false });
  const handleYapeFormDataChange = useCallback((formData) => {
    yapeFormRef.current = { ...yapeFormRef.current, formData };
  }, []);
  const handleYapeValidationChange = useCallback((isValid) => {
    yapeFormRef.current = { ...yapeFormRef.current, isValid };
  }, []);
  const { engineHooks, enginesLoading } = usePaymentEngines({
    purchase,
    env,
    purchaser,
    actions,
    cardCheckoutMode,
  });
  const { handlePayment, isProcessing } = usePaymentHandler({
    purchase: {
      ...purchase,
      isUpdating: isUpdating || updatingInsurance,
      selectedPaymentMethod,
      cardCheckoutMode,
      paymentEngine,
    },
    env,
    purchaser,
    actions,
    engineHooks,
    yapeFormRef,
  });
  // Drives the button spinner (isLoading): any in-flight/updating state.
  const basisTheoryLoading =
    cardCheckoutMode === 'basis_theory' &&
    (basisTheoryStatus === 'loading' || basisTheoryStatus === 'submitting');
  const basisTheoryUnavailable =
    cardCheckoutMode === 'basis_theory' && basisTheoryStatus !== 'ready';
  const buttonLoading =
    fetchingPayment ||
    isUpdating ||
    enginesLoading ||
    isProcessing ||
    isSyncingPurchaserForWallet ||
    updatingInsurance ||
    basisTheoryLoading ||
    // PRO2-128: the RUC lookup fills the read-only legal name asynchronously, and the two fields are
    // validated independently — so between a retyped id and its answer the form holds a new id beside
    // the PREVIOUS legal name and passes validation. Paying there invoices the new taxpayer under the
    // old name. `Checkout.js` has always folded this flag into its own button state; Quick Purchase
    // reached the invoice block for the first time in this change and had no equivalent.
    //
    // Coerced: this is the last operand, so an `undefined` here would make `buttonLoading` itself
    // `undefined` rather than `false`, which two existing tests caught.
    Boolean(fetchingTaxpayerId);

  // Addi (Yuno BNPL) only accepts Cédula de Ciudadanía. Folded into
  // paymentBlocked (not buttonLoading) so it disables the pay button and guards
  // the payment handlers without turning on the spinner, which tracks
  // buttonLoading only.
  const addiBlocked = isAddiBlockedByDocument({
    selectedPaymentMethod,
    purchaserDocumentType: purchaserFormValues?.documentType,
  });
  // Drives the disabled state (isDisabled) and the internal payment guards:
  // either something is loading or the payment is not allowed to proceed.

  // Gontijo sells to signed-in buyers only: the backend reads `client_auth` off the ticket and the
  // payment, and issues neither without it. Folded in here rather than guarded at the button so it
  // also covers the programmatic entries into continuePayment — the wallet sheets and the deferred
  // resume paths reach payment without a rendered click. Every other brand reads false.
  const gontijoSessionMissing = useSelector(selectGontijoPaymentBlocked);

  const paymentBlocked =
    buttonLoading || basisTheoryUnavailable || addiBlocked || gontijoSessionMissing;
  useEffect(() => basisTheoryCheckoutEngine.subscribe(setBasisTheoryStatus), []);

  // Reports what the purchase actually holds at the moment of a payment attempt. Placed in
  // continuePayment because it is the single point every payment type passes through — the PayPal v6
  // and wallet branches below return before handlePayment, so reporting there would miss them.
  // Always tracks (an incomplete rate needs a denominator) and only alerts when something is off.
  // Fail-open: a diagnostic must never be the reason a payment does not go through.
  const reportPaymentAttemptData = useCallback(() => {
    try {
      const audit = buildPaymentAttemptDataAudit({ purchase, purchaserFormValues });

      quickPurchasePaymentAttemptTracker({
        token,
        paymentOption: selectedPaymentOption,
        ...audit,
      });

      if (audit.hasIncompleteData) {
        errorMonitoring.notify({
          error: new Error('Quick purchase payment attempt with incomplete buyer/passenger data'),
          info: {
            from: 'usePaymentSection:paymentAttempt',
            token,
            paymentOption: selectedPaymentOption,
            missingFields: audit.missingFields,
            unsavedBuyerFields: audit.unsavedBuyerFields,
            buyer: audit.buyer,
            buyerForm: audit.buyerForm,
            passengers: audit.passengers,
          },
        });
      }
    } catch (error) {
      // The reporter of a reporting failure can fail too (a broken transport takes both the tracker
      // and notify down together). Swallow it: nothing here is worth blocking a payment for.
      try {
        errorMonitoring.notify({
          error,
          info: { from: 'usePaymentSection:reportPaymentAttemptData', token },
        });
      } catch (notifyError) {
        console.warn('payment attempt reporting failed', notifyError);
      }
    }
  }, [purchase, purchaserFormValues, selectedPaymentOption, token]);

  const continuePayment = useCallback(
    (paymentParams = {}) => {
      let current;
      try {
        current = requireCurrentLegal();
      } catch (error) {
        paymentParams.reject?.(error);
        return;
      }
      if (isGontijoPaymentBlockedNow()) {
        paymentParams.reject?.();
        return;
      }
      const protectedWallet =
        isWalletPaymentOption(selectedPaymentOption) &&
        (paymentParams.registerPurchaserSave || current.policy.hasAgreements);
      if (current.policy.hasAgreements && current.dirty && !protectedWallet) {
        synchronizePurchaserAndResume(paymentParams);
        return;
      }
      reportPaymentAttemptData();

      const isPaypalV6 = selectedPaymentOption === 'paypal' && isPayPalV6Enabled();

      if (isPaypalV6) {
        const { paypalType, paypalOptions = {} } = paymentParams;
        actions.onPaypalPaymentAttempt(paypalType, paypalOptions);
        return;
      }

      if (isWalletPaymentOption(selectedPaymentOption)) {
        if (paymentParams.resolve) {
          paymentParams.resolve();
        } else {
          console.warn(`${selectedPaymentOption}: paymentParams.resolve is missing`);
        }
        return;
      }

      handlePayment();
    },
    [
      actions,
      handlePayment,
      reportPaymentAttemptData,
      selectedPaymentOption,
      requireCurrentLegal,
      synchronizePurchaserAndResume,
    ],
  );

  const handlePaymentClick = useCallback(
    (paymentParams = {}) => {
      const params = normalizePaymentClickParams(paymentParams);
      const isWalletPayment = isWalletPaymentOption(selectedPaymentOption);
      const showPurchaserForm = features.PURCHASER_FORM_ON === 'CHECKOUT';
      const shouldSyncPurchaserBeforeWallet =
        showPurchaserForm &&
        !purchaserSubmitted &&
        !params.skipPurchaserCheck &&
        isWalletPayment &&
        typeof params.resolve === 'function' &&
        typeof params.reject === 'function';

      // The rendered `paymentBlocked` plus a fresh read of the session. The rendered value cannot
      // see a session that expired while the checkout sat open: nothing dispatches when a clock
      // passes an expiry, so `useSelector` never re-runs and the guard would wave the payment
      // through for `withClientAuth` to then strip the token from.
      if (paymentBlocked || isGontijoPaymentBlockedNow()) {
        params.reject?.();
        pendingPaymentParamsRef.current = null;
        return;
      }

      try {
        requireCurrentLegal();
      } catch (error) {
        params.reject?.(error);
        return;
      }
      failedAutoSubmissionRef.current = null;
      quickPurchaseMainButtonTracker();

      // Buyer validity gate. It must run BEFORE the onBeforePaymentAsync branch below: that branch
      // returns early and later resumes with `skipPurchaserCheck: true`, so the purchaser submit —
      // and with it `purchaserValidations` — was never reached while the passengers form was open
      // (opened by the user, or automatically for incomplete passenger data per CON-1076). Payment
      // then went through with a blank buyer phone and failed downstream. Gating here also means an
      // invalid buyer never triggers the passenger save, so the purchase is not left partially
      // updated. Mirrors the wallet gate in Checkout.js, including its `=== false` check: only a
      // positive invalid result blocks, so an uncomputable validity preserves prior behavior rather
      // than locking the button.
      if (showPurchaserForm && !params.skipPurchaserCheck && isPurchaserFormValid === false) {
        params.reject?.();
        pendingPaymentParamsRef.current = null;
        // Submitting marks the fields touched so they render in error state; onFormFail raises the
        // "check the fields in red" warning.
        actions.onSubmitForm('purchaser');
        actions.onFormFail();
        return;
      }

      if (
        quickCheckoutAgreementPolicy?.hasAgreements &&
        isWalletPayment &&
        (params.registerPurchaserSave || readCurrentAgreements().policy.hasAgreements)
      ) {
        walletNeedsPassengerSaveRef.current = Boolean(onBeforePaymentAsync);
        params.registerPurchaserSave?.(beforeWalletPayment);
        continuePayment(params);
        return;
      }

      if (onBeforePaymentAsync) {
        onBeforePaymentAsync()
          .then(() => {
            // Passenger save already handled the purchaser update inside the thunk.
            // Defer the actual payment to the effect below so it resumes from a fresh
            // render with up-to-date state/handlers instead of these stale closures.
            pendingAfterPassengersParamsRef.current = params;
            setPendingAfterPassengers(true);
          })
          .catch((error) => {
            // The params still have to be rejected either way — that is what releases the pay
            // button and stops the charge. Only the report is conditional: a buyer declining the
            // discount-loss prompt, a passenger form failing its own validation, and a save
            // superseded by a newer one are outcomes of this flow, not defects, and reporting them
            // buried the real failures in noise.
            if (!isExpectedPaymentCancellation(error)) {
              errorMonitoring.notify({
                error,
                info: { from: 'usePaymentSection:onBeforePaymentAsync' },
              });
            }
            params.reject?.(error);
          });
        return;
      }

      if (showPurchaserForm && !purchaserSubmitted && !params.skipPurchaserCheck) {
        // Wallet validity is already enforced by the buyer gate above, so a wallet reaching here is
        // valid. Adyen Apple Pay and Stripe's express wallets must open their sheet inside the click
        // gesture: saving first and resolving after the PUT loses it (Apple Pay throws "Must create a
        // new ApplePaySession from a user gesture handler"). Since a post-save edit flips the buyer
        // back to unsaved (PRO2-146), this is reachable on any click after an edit. Open the sheet
        // now and hand the wallet the save, which it awaits at confirm before the charge — mirrors
        // Checkout.js. Adyen Google Pay passes no registerPurchaserSave and keeps the path below.
        if (isWalletPayment && typeof params.registerPurchaserSave === 'function') {
          // What this save sends is what the parent records as saved, as in handleSubmitPurchaser.
          const sentValues = purchaserFormValues;
          /**
           * Saves the buyer as it stood at the click. The wallet awaits it at confirm and fails the
           * sheet on a rejection, so the charge never runs against unsaved buyer data. The wallets
           * do not report that rejection, so it is reported here, as handleSubmitPurchaser does.
           * @returns {Promise<void>} Settles once the save has been recorded with the parent.
           */
          const savePurchaser = () =>
            actions
              .onSetPurchaser(token, sentValues)
              .then(() => onPurchaserSubmitted?.(sentValues))
              .catch((error) => {
                errorMonitoring.notify({
                  error,
                  info: { from: 'usePaymentSection:deferredWalletPurchaserSave', token },
                });
                throw error;
              });
          params.registerPurchaserSave(savePurchaser);
          continuePayment(params);
          return;
        }

        if (shouldSyncPurchaserBeforeWallet) {
          pendingWalletPaymentParamsRef.current = {
            resolve: params.resolve,
            reject: params.reject,
            paymentOption: selectedPaymentOption,
          };
          setIsSyncingPurchaserForWallet(true);
          actions.onSubmitForm('purchaser');
          return;
        }

        if (isWalletPayment && params.reject) {
          params.reject();
        }

        pendingPaymentParamsRef.current = params;
        pendingPaymentOptionRef.current = selectedPaymentOption;
        actions.onSubmitForm('purchaser');
        if (isWalletPayment) {
          actions.onFormFail();
        }
        return;
      }

      if (quickCheckoutAgreementPolicy?.hasAgreements && readCurrentAgreements().dirty) {
        synchronizePurchaserAndResume(params);
        return;
      }
      continuePayment(params);
    },
    [
      actions,
      continuePayment,
      features.PURCHASER_FORM_ON,
      isPurchaserFormValid,
      paymentBlocked,
      purchaserFormValues,
      purchaserSubmitted,
      onBeforePaymentAsync,
      onPurchaserSubmitted,
      selectedPaymentOption,
      requireCurrentLegal,
      readCurrentAgreements,
      synchronizePurchaserAndResume,
      beforeWalletPayment,
      quickCheckoutAgreementPolicy,
      token,
    ],
  );

  const handleSubmitPurchaser = useCallback(() => {
    const pendingWallet = pendingWalletPaymentParamsRef.current;
    pendingWalletPaymentParamsRef.current = null;
    // What this save sends is what the parent records as saved (PRO2-146), so a later edit — or one
    // made while this request is in flight — is compared against the request body, not against
    // whatever the form holds when the promise settles.
    const sentValues = purchaserFormValues;
    purchaserSavesInFlightRef.current += 1;
    let settled = false;
    /**
     * Releases this save's in-flight count exactly once, whichever way the save ends: success, a
     * rejection, a success branch that throws into the catch, or a synchronous throw.
     * @returns {void}
     */
    const markSettled = () => {
      if (settled) return;
      settled = true;
      purchaserSavesInFlightRef.current -= 1;
    };

    let saveRequest;
    try {
      saveRequest = quickCheckoutAgreementPolicy?.hasAgreements
        ? synchronizePurchaser(true)
        : actions.onSetPurchaser(token, sentValues);
    } catch (error) {
      // Never reaches the handlers below. Release the count, or no parked payment could ever resume,
      // and rethrow so redux-form reports the failed submit as before.
      markSettled();
      // The parked wallet was already taken out of its ref above, so the submit-fail handler
      // redux-form runs next finds nothing to reject. Cancel it here, or its sheet never settles.
      pendingWallet?.reject?.();
      throw error;
    }

    saveRequest
      .then((savedValues) => {
        const completedValues = quickCheckoutAgreementPolicy?.hasAgreements
          ? savedValues
          : sentValues;
        const editedDuringSave = !purchaserValuesMatch(
          completedValues,
          latestPurchaserFormValuesRef.current,
        );
        // Refs first, state after. In React 16 a setState inside a promise callback renders
        // synchronously and flushes pending passive effects first, so by the time any setState
        // below runs, the resume effect must already see the final pending payment and the released
        // in-flight count.
        if (editedDuringSave) {
          // The buyer edited the form while this save was in flight, so the purchase now holds
          // older data than the screen. Drop the payment parked behind the save instead of letting
          // it start later with no click (when the buyer happens to restore the saved value); the
          // next click saves the newer data first.
          pendingPaymentParamsRef.current = null;
          pendingWallet?.reject?.();
        } else if (pendingWallet) {
          pendingPaymentParamsRef.current = {
            skipPurchaserCheck: true,
            resolve: pendingWallet.resolve,
            reject: pendingWallet.reject,
          };
          pendingPaymentOptionRef.current = pendingWallet.paymentOption;
        }
        markSettled();

        if (editedDuringSave || pendingWallet) setIsSyncingPurchaserForWallet(false);
        if (!quickCheckoutAgreementPolicy?.hasAgreements) onPurchaserSubmitted?.(sentValues);
        if (purchaserSavesInFlightRef.current === 0 && pendingPaymentParamsRef.current) {
          setLastPurchaserSaveSettled((settledCount) => settledCount + 1);
        }
      })
      .catch((error) => {
        markSettled();
        setIsSyncingPurchaserForWallet(false);
        pendingPaymentParamsRef.current?.reject?.(error);
        pendingPaymentParamsRef.current = null;
        pendingWallet?.reject?.(error);
        if (quickCheckoutAgreementPolicy?.hasAgreements) {
          failedAutoSubmissionRef.current =
            error.message === 'legal_terms_revoked'
              ? null
              : JSON.stringify(currentRef.current.purchaserFormValues);
          autoSubmittedPurchaserRef.current = false;
        }
        if (!isExpectedPaymentCancellation(error)) {
          errorMonitoring.notify({
            error,
            info: { from: 'usePaymentSection:submitPurchaser', token },
          });
        }
      });
    // The canonical writer dispatches backend errors. Returning a raw async chain lets
    // redux-form clear those errors with a later stopSubmit, so preserve the existing host contract.
  }, [
    actions,
    token,
    purchaserFormValues,
    onPurchaserSubmitted,
    quickCheckoutAgreementPolicy,
    synchronizePurchaser,
  ]);

  const handlePurchaserSubmitFail = useCallback(() => {
    const pendingWallet = pendingWalletPaymentParamsRef.current;
    pendingWalletPaymentParamsRef.current = null;
    setIsSyncingPurchaserForWallet(false);
    pendingPaymentParamsRef.current = null;
    if (currentRef.current.quickCheckoutAgreementPolicy?.hasAgreements) {
      failedAutoSubmissionRef.current = JSON.stringify(currentRef.current.purchaserFormValues);
      autoSubmittedPurchaserRef.current = false;
    }

    if (pendingWallet?.reject) {
      pendingWallet.reject();
    }
  }, []);

  const handleCardSubmit = useCallback(() => {
    const paymentInfo = {
      amount: total,
      currency: env.currency,
    };
    actions.onCardPaymentAttempt(token, cardValues, paymentInfo);
  }, [total, env.currency, actions, token, cardValues]);

  const handleTransferSubmit = useCallback(
    (transferData) => {
      actions.onTransferPaymentAttempt(transferData);
    },
    [actions],
  );

  const handlePaymentTypeChange = useCallback(
    (option, method) => {
      actions.onSelectPaymentOption(option, method);
    },
    [actions],
  );

  // Pre-submit the purchaser form when PayPal v6 is selected so the SDK button works in a
  // single click. PayPal's popup must open within the user-activation window of the click;
  // routing the click through an async purchaser update consumes that window and the popup
  // is suppressed silently — forcing the user to click twice.
  //
  // Once per PayPal selection, not once per checkout (PRO2-146): leaving PayPal re-arms it, so a
  // buyer who edits their data under another method and comes back gets the edit saved before the
  // PayPal click. While PayPal stays selected it does not run again on every edit; an edit made
  // there is saved by the click itself (the unsaved-purchaser branch of handlePaymentClick).
  useEffect(() => {
    if (!(selectedPaymentOption === 'paypal' && isPayPalV6Enabled())) {
      autoSubmittedPurchaserRef.current = false;
      return;
    }
    if (isOrchestrating || orchestrationError) return;
    const current = readCurrentAgreements();
    if (current.policy.legalRequired && !current.legal) return;
    if (quickCheckoutAgreementPolicy?.hasAgreements) {
      // Optional agreement changes re-arm pre-saving, while buyer typing remains click-driven.
      if (features.PURCHASER_FORM_ON === 'CHECKOUT' && isPurchaserFormValid === false) return;
      const signature = JSON.stringify({
        promotionalTerms: current.values.promotionalTerms,
        cdsFrequentPassengerOptIn: current.policy.cdsFrequentPassengerOptInApplicable
          ? current.values.cdsFrequentPassengerOptIn
          : undefined,
        legal: current.policy.legalRequired ? current.legal : undefined,
      });
      if (autoSubmittedPurchaserRef.current === signature) return;
      if (failedAutoSubmissionRef.current === JSON.stringify(purchaserFormValues)) return;
      if (purchaserSubmitted && !current.dirty) {
        autoSubmittedPurchaserRef.current = signature;
        return;
      }
      if (!purchaserFormValues?.purchaserFirstName && !current.dirty) return;
      autoSubmittedPurchaserRef.current = signature;
    } else {
      if (autoSubmittedPurchaserRef.current) return;
      // Selected with the buyer already saved: this selection needs no save. Arm the guard anyway, or
      // the buyer's first edit under PayPal would flip `purchaserSubmitted` and save mid-typing.
      if (purchaserSubmitted) {
        autoSubmittedPurchaserRef.current = true;
        return;
      }
      if (!purchaserFormValues?.purchaserFirstName) return;
      autoSubmittedPurchaserRef.current = true;
    }
    actions.onSubmitForm('purchaser');
  }, [
    isOrchestrating,
    orchestrationError,
    purchaserSubmitted,
    selectedPaymentOption,
    purchaserFormValues,
    actions,
    readCurrentAgreements,
    legalTerms,
    quickCheckoutAgreementPolicy,
    isPurchaserFormValid,
    features.PURCHASER_FORM_ON,
  ]);

  useEffect(() => {
    const pendingPaymentParams = pendingPaymentParamsRef.current;
    if (!pendingPaymentParams) return;
    if (pendingPaymentOptionRef.current !== selectedPaymentOption) {
      // The buyer switched method while the save was in flight. Drop the payment and release a
      // waiting wallet sheet; the next click pays with the method now selected.
      pendingPaymentParamsRef.current = null;
      pendingPaymentParams.reject?.();
      return;
    }
    if (!purchaserSubmitted || paymentBlocked) return;
    if (purchaserSavesInFlightRef.current > 0) return;

    pendingPaymentParamsRef.current = null;

    continuePayment({
      ...pendingPaymentParams,
      skipPurchaserCheck: true,
    });
  }, [
    continuePayment,
    lastPurchaserSaveSettled,
    paymentBlocked,
    purchaserSubmitted,
    selectedPaymentOption,
  ]);

  // Resume payment after a passenger edit save once the store has settled and payment is
  // unblocked. Runs from a fresh render so continuePayment/handlePayment use the latest
  // purchase, engineHooks and loading flags instead of stale click-time closures. The save
  // relock preserves the payment selection (preservePaymentOptionSelection in getPurchase),
  // so the option/card form survive. The purchaser update was already handled inside the
  // passenger save thunk, so skip its check.
  useEffect(() => {
    if (!pendingAfterPassengers || paymentBlocked) return;

    const pendingPaymentParams = pendingAfterPassengersParamsRef.current || {};
    pendingAfterPassengersParamsRef.current = null;
    setPendingAfterPassengers(false);

    if (quickCheckoutAgreementPolicy?.hasAgreements) {
      synchronizePurchaserAndResume({ ...pendingPaymentParams, skipPurchaserCheck: true });
    } else {
      continuePayment({ ...pendingPaymentParams, skipPurchaserCheck: true });
    }
  }, [
    continuePayment,
    synchronizePurchaserAndResume,
    quickCheckoutAgreementPolicy,
    paymentBlocked,
    pendingAfterPassengers,
  ]);

  useEffect(() => {
    if (!pendingAgreements || paymentBlocked) return;
    setPendingAgreements(false);
    const params = pendingAgreementParamsRef.current;
    pendingAgreementParamsRef.current = null;
    if (params && pendingAgreementOptionRef.current !== selectedPaymentOption) {
      params.reject?.();
      return;
    }
    if (params) continuePayment(params);
  }, [continuePayment, paymentBlocked, pendingAgreements, selectedPaymentOption]);

  const yapeFormProps = useMemo(
    () => ({
      onYapeFormDataChange: handleYapeFormDataChange,
      onYapeValidationChange: handleYapeValidationChange,
      isYapeSubmitting: isProcessing,
    }),
    [handleYapeFormDataChange, handleYapeValidationChange, isProcessing],
  );

  return {
    handlePaymentClick,
    handleSubmitPurchaser,
    handlePurchaserSubmitFail,
    handleCardSubmit,
    handleTransferSubmit,
    handlePaymentTypeChange,
    buttonLoading,
    paymentBlocked,
    quickCheckoutAgreementsBlocked: Boolean(
      quickCheckoutAgreementPolicy?.legalRequired && !legalTerms,
    ),
    beforeWalletPayment,
    yapeFormProps,
  };
}

export default usePaymentSection;
